/**
 * The trimmable-lot minimum for NEW option lines (CIO, 2026-09-17).
 *
 * A one-contract line can never be half-trimmed at 2x with a runner kept, so it becomes a
 * hold-or-close decision. The rule "no new one-contract lines" is enforced by the SERVER —
 * HelionBackend/strategy/lot_minimum.py, applied in create_order_ticket,
 * pending_order_disposition and record_discretionary_trade. This module is only:
 *
 *   1. the Screener's PREVIEW, so a row below the minimum asks for an exception reason before
 *      a ticket is attempted rather than after a refusal. It mirrors evaluate_ticket_buy and
 *      reads the rule the server publishes (home_run_harvest `lotMinimum`) — never a
 *      hardcoded 2: at a 40% trim the minimum is 3.
 *   2. the server's literals (refusal codes, statuses, resolution keys) in one place, so the
 *      Screener and the Trade Desk cannot drift from each other. lotMinimum.test.ts pins them
 *      against the Python source, and test_lot_minimum.py pins the Python side.
 *
 * When the rule could not be read the preview says "unknown" and blocks nothing: the server
 * still refuses, and the Screener then shows the exception box from the refusal itself.
 */

/** Refusal codes (strategy/lot_minimum.py CODE_*). */
export const LOT_MINIMUM_EXCEPTION_REQUIRED = "lot_minimum_exception_required";
export const LOT_MINIMUM_RESOLUTION_REQUIRED = "lot_minimum_resolution_required";
/** An executed option BUY recorded with no fill quantity (the fill decides the minimum). */
export const LOT_MINIMUM_FILL_REQUIRED = "lot_minimum_fill_required";

/** Recorded-BUY statuses that leave a line below the minimum (strategy/lot_minimum.py BELOW_MINIMUM_STATUSES). */
export const BELOW_MINIMUM_STATUSES: readonly string[] = [
  "below_minimum_after_fill",
  "below_minimum_unverified",
  "exception_on_ticket",
];

/** A numeric IBKR conid: the only tickets the lot minimum applies to (ETF tickets carry a ticker). */
export const isOptionContractId = (contractId: unknown): boolean => /^\d+$/.test(String(contractId ?? "").trim());

/** The server's create_order_ticket refusal for one Screener row, at the quantity it judged. */
export type LotRefusal = { qty: number; message: string };

/**
 * The server outranks the preview. The preview is built from a held book read when the page
 * loaded; if a line was closed since, the preview says "top-up" while the server says "new line".
 * A refusal therefore opens the reason box whatever the preview said — but only for the quantity
 * it was about, so fixing the quantity clears it instead of asking for a reason nobody needs.
 */
export function applyLotRefusal(preview: ScreenerLotPreview, refusal: LotRefusal | null | undefined, qty: number): ScreenerLotPreview {
  if (!refusal || refusal.qty !== Math.trunc(qty)) return preview;
  if (preview.requiresException) return preview.message ? preview : { ...preview, message: refusal.message };
  return { ...preview, requiresException: true, message: refusal.message || preview.message };
}

/** Resolution keys (strategy/lot_minimum.py RESOLUTION_*). */
export type LotResolution = "exception" | "will_top_up" | "adds_to_existing_line";

export type LotMinimumRule = {
  enforced: boolean;
  minContracts: number;
  heldBookReadable: boolean;
  heldByContract: Record<string, number>;
};

/** Read home_run_harvest's `lotMinimum` block. Null when absent or malformed — never a default. */
export function readLotMinimumRule(harvestPayload: unknown): LotMinimumRule | null {
  const block = (harvestPayload as { lotMinimum?: unknown } | null | undefined)?.lotMinimum as
    | Record<string, unknown>
    | null
    | undefined;
  if (!block || typeof block !== "object") return null;
  const min = Number(block.minContracts);
  if (typeof block.enforced !== "boolean" || !Number.isInteger(min) || min < 1) return null;
  const held: Record<string, number> = {};
  const raw = block.heldByContract;
  if (raw && typeof raw === "object") {
    for (const [conid, qty] of Object.entries(raw as Record<string, unknown>)) {
      const n = Number(qty);
      if (Number.isFinite(n) && n > 0) held[String(conid)] = n;
    }
  }
  return {
    enforced: block.enforced,
    minContracts: min,
    heldBookReadable: block.heldBookReadable === true,
    heldByContract: held,
  };
}

export type ScreenerLotPreview = {
  status:
    | "unknown"
    | "not_enforced"
    | "meets_minimum"
    | "top_up"
    | "below_minimum_new_line"
    | "below_minimum_book_unreadable";
  requiresException: boolean;
  /** Contracts already held on this line; null when the book could not be read. */
  heldQty: number | null;
  minContracts: number | null;
  message: string;
};

/**
 * Mirror of evaluate_ticket_buy for one row. `qty` is what the CIO typed (or the allocator's
 * size). Options only: the Screener stages option conids.
 */
export function previewScreenerBuy(rule: LotMinimumRule | null, conid: string | number, qty: number): ScreenerLotPreview {
  if (!rule) {
    return { status: "unknown", requiresException: false, heldQty: null, minContracts: null, message: "" };
  }
  const min = rule.minContracts;
  const heldQty = rule.heldBookReadable ? rule.heldByContract[String(conid)] ?? 0 : null;
  const base = { heldQty, minContracts: min };
  const n = Number.isFinite(qty) ? Math.max(0, Math.trunc(qty)) : 0;
  const plural = (k: number) => `${k} contract${k === 1 ? "" : "s"}`;

  if (!rule.enforced) return { ...base, status: "not_enforced", requiresException: false, message: "" };
  if (n >= min) return { ...base, status: "meets_minimum", requiresException: false, message: "" };
  if (heldQty != null && heldQty > 0) {
    const after = heldQty + n;
    return {
      ...base,
      status: "top_up",
      requiresException: false,
      message:
        after < min
          ? `Adds to the ${heldQty} you hold, but ${after} is still below the ${min}-contract minimum — still no 2x trim.`
          : `Adds to the ${heldQty} you hold → ${after}, which can be trimmed at 2x.`,
    };
  }
  if (heldQty == null) {
    return {
      ...base,
      status: "below_minimum_book_unreadable",
      requiresException: true,
      message: `${plural(n)} is below the ${min}-contract minimum, and the held book could not be read to confirm this adds to a line you hold. Treated as a new line: it could never be trimmed at 2x.`,
    };
  }
  return {
    ...base,
    status: "below_minimum_new_line",
    requiresException: true,
    message: `A new line of ${plural(n)} is below the ${min}-contract minimum: at 2x it cannot be half-trimmed with a runner kept.`,
  };
}

/** The server's refusal body for a recorded BUY that needs a resolution. */
export type LotResolutionFlag = {
  status: string;
  message: string;
  minContracts: number | null;
  recordedQty: number | null;
  allowedResolutions: LotResolution[];
  resolutionLabels: Partial<Record<LotResolution, string>>;
};

const RESOLUTIONS: readonly LotResolution[] = ["exception", "will_top_up", "adds_to_existing_line"];

/**
 * Parse a 422 from pending_order_disposition / record_discretionary_trade. Null unless the
 * body is that specific refusal — any other error must render as an error, not a choice.
 */
export function readLotResolutionFlag(body: unknown): LotResolutionFlag | null {
  const b = body as { code?: unknown; error?: unknown; lotMinimum?: Record<string, unknown> } | null | undefined;
  if (!b || b.code !== LOT_MINIMUM_RESOLUTION_REQUIRED || !b.lotMinimum || typeof b.lotMinimum !== "object") return null;
  const lot = b.lotMinimum;
  const allowed = Array.isArray(lot.allowedResolutions)
    ? (lot.allowedResolutions as unknown[]).filter((r): r is LotResolution => RESOLUTIONS.includes(r as LotResolution))
    : [];
  if (!allowed.length) return null;
  const labelsIn = (lot.resolutionLabels && typeof lot.resolutionLabels === "object" ? lot.resolutionLabels : {}) as Record<string, unknown>;
  const labels: Partial<Record<LotResolution, string>> = {};
  for (const r of allowed) labels[r] = typeof labelsIn[r] === "string" ? (labelsIn[r] as string) : r;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return {
    status: String(lot.status ?? ""),
    message: typeof lot.message === "string" && lot.message ? lot.message : String(b.error ?? ""),
    minContracts: num(lot.minContracts),
    recordedQty: num(lot.recordedQty),
    allowedResolutions: allowed,
    resolutionLabels: labels,
  };
}

/** True when the Screener's create_order_ticket refusal is the lot-minimum one. */
export function isLotExceptionRefusal(body: unknown): boolean {
  return (body as { code?: unknown } | null | undefined)?.code === LOT_MINIMUM_EXCEPTION_REQUIRED;
}

/** The standing statement of the rule, for the Screener's queue bar. */
export type LotRuleLine = {
  /** "on" | "off" | "unknown" — the same vocabulary as the Signals controls line. */
  state: "on" | "off" | "unknown";
  text: string;
};

/**
 * One sentence saying whether the contract minimum is live, shown whether or not anything is
 * ticked (CIO, 2026-09-17).
 *
 * WHY. Until now the rule spoke only at the moment of refusal: tick a row, size it at 1, and a
 * warning appears. On every other run the Screener said nothing about it, so a live control was
 * indistinguishable from an absent one — the CIO ran the page, saw no trace of the minimum, and
 * reasonably asked whether it had been built at all.
 *
 * ABSENCE IS NOT A GREEN LIGHT. A null rule means home_run_harvest did not load or predates the
 * block; this page then previews nothing, and create_order_ticket remains the gate. That says
 * "unknown", never silence and never "on".
 */
export function describeLotRule(rule: LotMinimumRule | null | undefined): LotRuleLine {
  if (!rule) {
    return {
      state: "unknown",
      text: "Contract minimum unknown — this page could not read the rule. The ticket service still enforces it.",
    };
  }
  if (!rule.enforced) {
    return {
      state: "off",
      text: "Contract minimum is OFF — a new line can be opened at any size, including one that can never be trimmed at 2x.",
    };
  }
  return {
    state: "on",
    text: `New option lines open with at least ${rule.minContracts} contract${rule.minContracts === 1 ? "" : "s"}, so a 2x half-trim can leave a runner. Fewer, and the row asks for an exception reason.`,
  };
}
